/**
 * Owner: Masa Bayu
 * File: oauth-google.ts
 * Date:Time: 2026-09-27 15:32
 * Description: Google Identity Services — qxread's own GIS Web client (GCP "qxread").
 */

import { QXREAD_GOOGLE_GIS_CLIENT_ID } from './google-client-ids';

const GIS_SRC = 'https://accounts.google.com/gsi/client';

type CredentialResponse = { credential?: string };

type GoogleAccountsId = {
  initialize: (config: {
    client_id: string;
    callback: (response: CredentialResponse) => void;
    auto_select?: boolean;
    cancel_on_tap_outside?: boolean;
  }) => void;
  prompt: (
    listener?: (notification: {
      isNotDisplayed: () => boolean;
      isSkippedMoment: () => boolean;
      getNotDisplayedReason: () => string;
    }) => void,
  ) => void;
  renderButton: (
    parent: HTMLElement,
    options: { type?: string; theme?: string; size?: string; text?: string; width?: number },
  ) => void;
};

declare global {
  interface Window {
    google?: { accounts?: { id?: GoogleAccountsId } };
  }
}

let scriptPromise: Promise<void> | null = null;

function isSafariOrIos(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  if (/iPhone|iPad|iPod/i.test(ua)) return true;
  return /Safari/i.test(ua) && !/Chrome|CriOS|FxiOS|EdgiOS/i.test(ua);
}

function loadGis(): Promise<void> {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('browser only'));
  }
  if (window.google?.accounts?.id) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${GIS_SRC}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('Google Sign-In failed to load')));
      return;
    }
    const script = document.createElement('script');
    script.src = GIS_SRC;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Google Sign-In failed to load'));
    document.head.appendChild(script);
  });
  return scriptPromise;
}

function originRegistrationHint(): string {
  const origin =
    typeof window === 'undefined'
      ? 'https://qxread.com'
      : window.location.origin.replace(/\/$/, '');
  return [
    'Google blocked sign-in (origin_mismatch).',
    `Add ${origin} under Authorized JavaScript origins on the QXRead Web client`,
    `…${QXREAD_GOOGLE_GIS_CLIENT_ID.slice(-12)} (GCP project "qxread").`,
    'No trailing slash. Wait ~5 min after Save.',
  ].join(' ');
}

function promptViaButton(id: GoogleAccountsId): Promise<string> {
  return new Promise((resolve, reject) => {
    const host = document.createElement('div');
    host.style.position = 'fixed';
    host.style.left = '-9999px';
    host.style.top = '0';
    host.setAttribute('aria-hidden', 'true');
    document.body.appendChild(host);

    const cleanup = () => {
      host.remove();
    };

    id.initialize({
      client_id: QXREAD_GOOGLE_GIS_CLIENT_ID,
      auto_select: false,
      cancel_on_tap_outside: true,
      callback: (response) => {
        cleanup();
        const token = response.credential?.trim();
        if (token) resolve(token);
        else reject(new Error('Google Sign-In returned no credential'));
      },
    });

    try {
      id.renderButton(host, { type: 'standard', theme: 'outline', size: 'large' });
    } catch {
      cleanup();
      reject(new Error(`Google Sign-In button unavailable. ${originRegistrationHint()}`));
      return;
    }

    const clickable =
      host.querySelector<HTMLElement>("[role='button']") ?? host.querySelector<HTMLElement>('div');
    if (!clickable) {
      cleanup();
      reject(new Error(`Google Sign-In button unavailable. ${originRegistrationHint()}`));
      return;
    }
    clickable.click();
  });
}

function promptViaOneTap(id: GoogleAccountsId): Promise<string> {
  return new Promise((resolve, reject) => {
    let settled = false;
    const finish = (fn: () => void) => {
      if (settled) return;
      settled = true;
      fn();
    };

    id.initialize({
      client_id: QXREAD_GOOGLE_GIS_CLIENT_ID,
      auto_select: false,
      cancel_on_tap_outside: true,
      callback: (response) => {
        const token = response.credential?.trim();
        if (token) finish(() => resolve(token));
        else finish(() => reject(new Error('Google Sign-In returned no credential')));
      },
    });

    id.prompt((notification) => {
      if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
        finish(() =>
          reject(
            new Error(
              `Google Sign-In unavailable (${notification.getNotDisplayedReason() || 'skipped'}). ${originRegistrationHint()}`,
            ),
          ),
        );
      }
    });
  });
}

/** GIS `width` is pixels only · documented max 400 · oversize + CSS stretch clips the right edge. */
function googleContinueButtonWidth(host: HTMLElement): number {
  const measured = Math.floor(host.getBoundingClientRect().width);
  return Math.min(400, Math.max(200, measured > 0 ? measured : 280));
}

/** Visible GIS button. The click stays inside Google's iframe, using the QXRead GIS client (§413). */
export async function mountGoogleContinueButton(
  parent: HTMLElement,
  onCredential: (idToken: string) => void,
): Promise<void> {
  await loadGis();
  const id = window.google?.accounts?.id;
  if (!id) throw new Error('Google Sign-In is unavailable in this browser');
  parent.replaceChildren();
  id.initialize({
    client_id: QXREAD_GOOGLE_GIS_CLIENT_ID,
    auto_select: false,
    cancel_on_tap_outside: true,
    callback: (response) => {
      const token = response.credential?.trim();
      if (token) onCredential(token);
    },
  });
  id.renderButton(parent, {
    type: 'standard',
    theme: 'outline',
    size: 'large',
    text: 'continue_with',
    width: googleContinueButtonWidth(parent),
  });
}

/** Account chooser → Google ID token via the QXRead GIS client (One Tap, Safari button fallback). */
export async function promptGoogleIdToken(): Promise<string> {
  await loadGis();
  const id = window.google?.accounts?.id;
  if (!id) throw new Error('Google Sign-In is unavailable in this browser');
  if (isSafariOrIos()) return promptViaButton(id);
  try {
    return await promptViaOneTap(id);
  } catch {
    return promptViaButton(id);
  }
}
